import { differenceBy, take, uniqBy } from 'lodash-es'

import {
  DataItemSublistItem,
  mapSublistItem,
} from '@/Atomic/Organisms/RelationalSublist'
import { Company } from '@/Data/Company/CompanyDataClass'
import { DataClassName } from '@/Data/const'
import { ContactContactRelation } from '@/Data/ContactContactRelation/ContactContactRelationDataClass'
import { ContactDocumentRelation } from '@/Data/ContactDocumentRelation/ContactDocumentRelationDataClass'
import { ContactProjectRelation } from '@/Data/ContactProjectRelation/ContactProjectRelationDataClass'
import { Person } from '@/Data/Person/PersonDataClass'
import { DataScopeOrder } from '@/Data/types'
import { useDataClassIndex } from '@/hooks/useDataClassIndex'
import { getAttributeDisplayValue } from '@/utils/CRUD/read/displayValue'
import { getSchemaAttributes } from '@/utils/CRUD/read/schema'
import { getSchemaName } from '@/utils/dataClassMap'
import { getVariantForEnumValue } from '@/utils/enumGroupVariant'
import { getDataItem } from '@/utils/getDataItem'
import { getRelatedContact } from '@/utils/getRelatedContact'
import { isString } from '@/utils/isString'

interface Args {
  subjectId: string
  search: string
  order: DataScopeOrder
  role: string | null
  contactType: string | null
  listSize: number
  contactRelation?: 'contact' | 'project' | 'document'
  contactEntity?: 'Person' | 'Company'
}

export const useContactTabData = ({
  subjectId,
  search,
  order,
  role,
  contactType,
  listSize,
  contactRelation = 'contact',
  contactEntity = 'Person',
}: Args) => {
  const isDocument = contactRelation === 'document'
  const dataClass = contactEntity === 'Company' ? Company : Person

  const relationClass =
    contactRelation === 'project'
      ? ContactProjectRelation
      : isDocument
        ? ContactDocumentRelation
        : ContactContactRelation
  const subjectKey =
    contactRelation === 'project'
      ? 'project'
      : isDocument
        ? 'document'
        : 'father'
  const additionalFilters =
    contactRelation === 'project' ? {} : { contactEntity }

  const {
    dataItems: relations,
    hasMore,
    loadMore,
    count,
  } = useDataClassIndex(
    relationClass,
    {
      filters: {
        [subjectKey]: subjectId,
        ...(role ? { role } : {}),
        ...(contactType ? { contactType } : {}),
        ...additionalFilters,
        contact_search: search,
      },
      order: [order],
    },
    listSize,
  )

  const schemaName = isDocument
    ? getSchemaName(DataClassName.CONTACT_DOCUMENT_RELATION)
    : getSchemaName(DataClassName.CONTACT_CONTACT_RELATION)
  const { schemaAttributes: relationSchemaAttributes } =
    getSchemaAttributes(schemaName)

  const mappedItems: DataItemSublistItem[] = relations.flatMap((relation) => {
    const { contactId, type: rawType } = getRelatedContact(relation, subjectId)
    if (!isString(contactId)) return []
    const { dataItem } = getDataItem(dataClass, contactId)
    if (!dataItem) return []
    const role = getAttributeDisplayValue(
      relationSchemaAttributes?.['role'],
      relation.get('role'),
    ) as string | undefined
    const type = getAttributeDisplayValue(
      relationSchemaAttributes?.['contactType'],
      rawType,
    ) as string | undefined
    const typeBadgeVariant = getVariantForEnumValue(
      relationSchemaAttributes?.['contactType']?.enumValues,
      rawType,
    )
    return {
      ...mapSublistItem(dataItem, relation),
      badgeValue: role,
      secondaryBadgeValue: type,
      secondaryBadgeVariant: typeBadgeVariant,
    }
  })

  const items = uniqBy(mappedItems, 'id')

  const { dataItems: searchResults } = useDataClassIndex(
    dataClass,
    { search },
    listSize,
  )

  const searchItems = searchResults.map((el) => ({
    ...mapSublistItem(el),
  }))
  const filteredSearchItems = differenceBy(searchItems, items, 'id')

  const shownSearchItems =
    search && items.length < listSize
      ? take(filteredSearchItems, listSize - items.length)
      : []

  // A full page of matches implies there may be more to narrow down; fewer than
  // a full page means we've surfaced every match, so the person likely isn't in
  // the system yet and we can offer to create one.
  const hasMoreSearchResults = searchResults.length >= listSize

  return {
    items,
    searchItems: shownSearchItems,
    hasMoreSearchResults,
    hasMore,
    loadMore,
    count,
  }
}
